import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Autocomplete,
  Box,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ApartmentIcon from '@mui/icons-material/Apartment';
import BusinessIcon from '@mui/icons-material/Business';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import UnitManagement from './UnitManagement';
import CommercialAllocationManagement from './CommercialAllocationManagement';
import EmptyState from '../EmptyState';
import FeeTermsFields from '../FeeTermsFields';
import { useCurrency } from '../../context/CurrencyContext';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';


// "1st", "2nd", "23rd" — a pay day reads as a date, not a count.
const ordinal = (n) => {
  const d = Number(n) || 1;
  const suffix = (d % 100 >= 11 && d % 100 <= 13) ? 'th' : (['th', 'st', 'nd', 'rd'][d % 10] || 'th');
  return `${d}${suffix}`;
};

const PropertyManagement = () => {
  const [properties, setProperties] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server must search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleProperties, resultCount, totalCount
  } = useTableSearch(properties, {
    searchFields: ['propertyName', 'addressStreet', 'landlord.name', 'propertyType'],
    filters: {
      status: (row, v) => row.status === v,
      propertyType: (row, v) => row.propertyType === v,
      landlordId: (row, v) => String(row.landlord?._id || row.landlord?.id || row.landlord) === v
    }
  });
  const [landlords, setLandlords] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showUnits, setShowUnits] = useState(false);
  const [showAllocations, setShowAllocations] = useState(false);
  const [formData, setFormData] = useState({
    propertyName: '',
    addressStreet: '',
    propertyType: 'apartment',
    numberOfUnits: 1,
    squareFeet: 0,
    status: 'available',
    landlord: '',
    paymentDay: 1,
    description: '',
  });
  const { currency } = useCurrency();

  useEffect(() => {
    fetchProperties();
    fetchLandlords();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/properties');
      setProperties(response.data);
    } catch (error) {
      toast.error('Error fetching properties');
    }
  };

  const fetchLandlords = async () => {
    try {
      const response = await axios.get('/landlords');
      setLandlords(response.data.filter(l => l.isActive));
    } catch (error) {
      toast.error('Error fetching landlords');
    }
  };

  const handleOpen = (property = null) => {
    if (property) {
      setEditing(property._id);
      setFormData({
        propertyName: property.propertyName || '',
        addressStreet: property.addressStreet || '',
        propertyType: property.propertyType || 'apartment',
        numberOfUnits: property.numberOfUnits || 1,
        squareFeet: property.squareFeet || 0,
        status: property.status || 'available',
        landlord: property.landlord?._id || property.landlord || '',
        paymentDay: property.paymentDay || 1,
        description: property.description || '',
        // '' rather than a default: blank means "inherit the landlord's percentage", which
        // is a different arrangement from an explicit 0%.
        feeType: property.feeType || '',
        feeValue: property.feeValue ?? '',
      });
    } else {
      setEditing(null);
      setFormData({
        propertyName: '',
        addressStreet: '',
        propertyType: 'apartment',
        numberOfUnits: 1,
        squareFeet: 0,
        status: 'available',
        landlord: '',
        paymentDay: 1,
        description: '',
        feeType: '',
        feeValue: '',
      });
    }
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await axios.put(`/properties/${editing}`, formData);
        toast.success('Property updated');
      } else {
        await axios.post('/properties', formData);
        toast.success('Property created');
      }
      setOpen(false);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving property');
    }
  };


  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Property Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Property
        </Button>
      </Box>

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Name, address or landlord"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
            { key: 'status', label: 'Status', value: filterValues.status || '',
              onChange: (v) => setFilter('status', v),
              options: [
                { value: 'available', label: 'Available' },
                { value: 'occupied', label: 'Occupied' },
                { value: 'maintenance', label: 'Maintenance' },
                { value: 'unavailable', label: 'Unavailable' }
              ] },
            { key: 'propertyType', label: 'Type', value: filterValues.propertyType || '',
              onChange: (v) => setFilter('propertyType', v),
              options: [
                { value: 'apartment', label: 'Apartment' },
                { value: 'house', label: 'House' },
                { value: 'condo', label: 'Condo' },
                { value: 'commercial', label: 'Commercial' }
              ] },
            { key: 'landlordId', label: 'Landlord', value: filterValues.landlordId || '',
              onChange: (v) => setFilter('landlordId', v), minWidth: 200,
              options: landlords.map((l) => ({ value: l._id, label: l.name })) }
        ]}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Property Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Landlord</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Units/Size</TableCell>
              <TableCell>Pay Day</TableCell>
              <TableCell>Status</TableCell>
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleProperties.map((property) => (
              <TableRow key={property._id}>
                <TableCell className="pin-left">{property.propertyName}</TableCell>
                <TableCell>
                  {property.addressStreet}
                </TableCell>
                <TableCell>
                  {property.landlord?.name || '-'}
                  {property.landlord && !property.landlord.isActive && (
                    <Chip label="Inactive" color="error" size="small" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={property.propertyType === 'commercial' ? 'Commercial' : 'Residential'} 
                    color={property.propertyType === 'commercial' ? 'secondary' : 'primary'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {property.propertyType === 'commercial' ? (
                    <Typography variant="body2">
                      {property.totalSquareFeet?.toLocaleString() || property.squareFeet?.toLocaleString() || 0} sq ft
                      {property.allocatedSquareFeet !== undefined && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {property.allocatedSquareFeet.toLocaleString()} allocated / {property.availableSquareFeet.toLocaleString()} available
                        </Typography>
                      )}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      {property.unitsCount || property.numberOfUnits || 0} units
                      {property.occupiedUnitsCount !== undefined && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {property.occupiedUnitsCount} occupied / {property.availableUnitsCount} available
                        </Typography>
                      )}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {ordinal(property.paymentDay || 1)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={property.status}
                    color={
                      property.status === 'occupied'
                        ? 'success'
                        : property.status === 'available'
                        ? 'primary'
                        : property.status === 'unavailable'
                        ? 'error'
                        : 'warning'
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell className="pin-right">
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleOpen(property)}
                    >
                      Edit
                    </Button>
                    {property.propertyType !== 'commercial' && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ApartmentIcon />}
                        onClick={() => {
                          setSelectedProperty(property);
                          setShowUnits(true);
                        }}
                      >
                        Units
                      </Button>
                    )}
                    {property.propertyType === 'commercial' && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<BusinessIcon />}
                        onClick={() => {
                          setSelectedProperty(property);
                          setShowAllocations(true);
                        }}
                      >
                        Allocations
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {visibleProperties.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState
                    icon={<ApartmentIcon sx={{ fontSize: 44 }} />}
                    title="No properties yet"
                    description="Add your first property to start tracking units, tenants, and rent."
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollTable>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editing ? 'Edit Property' : 'Add New Property'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Property Name"
                value={formData.propertyName}
                onChange={(e) =>
                  setFormData({ ...formData, propertyName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={landlords}
                value={landlords.find(l => l._id === formData.landlord) || null}
                getOptionLabel={(l) => `${l.name} (${l.type === 'company' ? 'Company' : 'Individual'})`}
                onChange={(_, val) => setFormData({
                  ...formData,
                  landlord: val?._id || '',
                  // Seed the pay day from the landlord's default on a NEW property only.
                  // On an edit the property's own day is what was agreed for it, and
                  // reaching back to the landlord would quietly overwrite that.
                  ...(editing ? {} : { paymentDay: val?.paymentDate || 1 })
                })}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                renderInput={(params) => (
                  <TextField {...params} label="Landlord" required />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.addressStreet}
                onChange={(e) => setFormData({ ...formData, addressStreet: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Property Type"
                required
                value={formData.propertyType}
                onChange={(e) =>
                  setFormData({ ...formData, propertyType: e.target.value })
                }
              >
                <MenuItem value="apartment">Apartment (Residential)</MenuItem>
                <MenuItem value="house">House (Residential)</MenuItem>
                <MenuItem value="condo">Condo (Residential)</MenuItem>
                <MenuItem value="commercial">Commercial</MenuItem>
              </TextField>
            </Grid>
            {formData.propertyType === 'commercial' ? (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Total Square Feet"
                  type="number"
                  required
                  value={formData.squareFeet}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) =>
                    setFormData({ ...formData, squareFeet: parseInt(e.target.value) || 0 })
                  }
                  helperText="Total square feet available for allocation"
                />
              </Grid>
            ) : (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Number of Units"
                  type="number"
                  required
                  min={1}
                  value={formData.numberOfUnits}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) =>
                    setFormData({ ...formData, numberOfUnits: parseInt(e.target.value) || 1 })
                  }
                  helperText="Number of units in this property"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Landlord Pay Day"
                value={formData.paymentDay || 1}
                onChange={(e) => setFormData({ ...formData, paymentDay: Number(e.target.value) })}
                helperText="Day of the month this property's landlord is paid. Each property is set separately."
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                    {/* 29–31 are accepted; short months fall back to the last day rather
                        than rolling into the next one. */}
                    {d > 28 ? ' (last day in shorter months)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <FeeTermsFields
              value={{ feeType: formData.feeType, feeValue: formData.feeValue }}
              onChange={(next) => setFormData({ ...formData, ...next })}
              inheritsFrom="the landlord's percentage"
              currency={currency}
            />
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="occupied">Occupied</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unit Management Dialog */}
      {selectedProperty && selectedProperty.propertyType !== 'commercial' && (
        <Dialog
          open={showUnits}
          onClose={() => {
            setShowUnits(false);
            setSelectedProperty(null);
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
          <DialogTitle>
            Manage Units - {selectedProperty?.propertyName}
          </DialogTitle>
          <DialogContent sx={{ overflow: 'auto' }}>
            <UnitManagement
              propertyId={selectedProperty?._id}
              onClose={() => {
                setShowUnits(false);
                setSelectedProperty(null);
                fetchProperties();
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Commercial Allocation Management Dialog */}
      {selectedProperty && selectedProperty.propertyType === 'commercial' && (
        <Dialog
          open={showAllocations}
          onClose={() => {
            setShowAllocations(false);
            setSelectedProperty(null);
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
          <DialogTitle>
            Manage Allocations - {selectedProperty?.propertyName}
          </DialogTitle>
          <DialogContent sx={{ overflow: 'auto' }}>
            <CommercialAllocationManagement
              propertyId={selectedProperty?._id}
              property={selectedProperty}
              onClose={() => {
                setShowAllocations(false);
                setSelectedProperty(null);
                fetchProperties();
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </Paper>
  );
};

export default PropertyManagement;

